import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { apiClient } from '~/lib/api';

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [commentDefaultStatus, setCommentDefaultStatus] = useState<string>('pending');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => apiClient.getSettings(),
  });

  useEffect(() => {
    if (data?.data) {
      setCommentDefaultStatus(data.data.commentDefaultStatus || 'pending');
    }
  }, [data]);

  const updateSettingsMutation = useMutation({
    mutationFn: (settings: { commentDefaultStatus: string }) => apiClient.updateSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
  });

  const clearFeaturedCacheMutation = useMutation({
    mutationFn: () => apiClient.clearFeaturedCache(),
  });

  const handleSave = () => {
    updateSettingsMutation.mutate({
      commentDefaultStatus,
    });
  };

  const handleClearFeaturedCache = () => {
    if (
      window.confirm(
        'Are you sure you want to clear the featured content cache? This will force all clients to reload featured content.'
      )
    ) {
      clearFeaturedCacheMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-3xl font-bold'>Settings</h1>
          <p className='text-muted-foreground'>Manage platform settings</p>
        </div>
        <div className='text-center py-8'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Settings</h1>
          <p className='text-muted-foreground'>Manage platform settings</p>
        </div>
        <Button onClick={handleSave} disabled={updateSettingsMutation.isPending}>
          <Save className='h-4 w-4 mr-2' />
          {updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {updateSettingsMutation.isSuccess && (
        <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
          <p className='text-sm text-green-800 dark:text-green-500'>Settings updated successfully!</p>
        </div>
      )}

      {updateSettingsMutation.isError && (
        <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
          <p className='text-sm text-red-800 dark:text-red-500'>Failed to update settings. Please try again.</p>
        </div>
      )}

      {/* Comment Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Comment Moderation</CardTitle>
          <CardDescription>Configure how comments are handled</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-2'>
            <label className='text-sm font-medium'>Default Comment Status</label>
            <Select value={commentDefaultStatus} onValueChange={setCommentDefaultStatus}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select default status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='pending'>
                  <div className='flex flex-col'>
                    <span className='font-medium'>Pending (Require Approval)</span>
                    <span className='text-xs text-muted-foreground'>Comments are hidden until approved by admin</span>
                  </div>
                </SelectItem>
                <SelectItem value='approved'>
                  <div className='flex flex-col'>
                    <span className='font-medium'>Approved (Auto-publish)</span>
                    <span className='text-xs text-muted-foreground'>Comments are visible immediately</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              {commentDefaultStatus === 'pending' ? (
                <>
                  <strong>Pending mode:</strong> New comments will be hidden from public view until an admin approves
                  them. Authors can still see their own pending comments.
                </>
              ) : (
                <>
                  <strong>Approved mode:</strong> New comments will be visible to everyone immediately. You can still
                  moderate and reject comments later.
                </>
              )}
            </p>
          </div>

          <div className='border-t pt-6'>
            <h4 className='font-semibold mb-2'>How it works:</h4>
            <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
              <li>
                <strong>Pending:</strong> Users see their own pending comments with a "Pending Approval" badge
              </li>
              <li>
                <strong>Approved:</strong> Comments are publicly visible to all users
              </li>
              <li>
                <strong>Rejected:</strong> Comments are hidden from everyone (set by admin in moderation panel)
              </li>
            </ul>
          </div>

          <div className='border-t pt-6'>
            <h4 className='font-semibold mb-2'>Moderation Tips:</h4>
            <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
              <li>Use "Pending" mode for high-quality control and preventing spam</li>
              <li>Use "Approved" mode for faster community engagement</li>
              <li>You can always review and moderate comments in the Comments page</li>
              <li>Changes apply to new comments only (existing comments keep their current status)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Cache Management */}
      <Card>
        <CardHeader>
          <CardTitle>Cache Management</CardTitle>
          <CardDescription>Manage cached content and force updates</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-start justify-between p-4 border rounded-lg'>
            <div className='flex-1'>
              <div className='flex items-center gap-2 mb-2'>
                <RefreshCw className='h-4 w-4 text-muted-foreground' />
                <h4 className='font-semibold'>Featured Content Cache</h4>
              </div>
              <p className='text-sm text-muted-foreground mb-3'>
                Clear the Redis cache for featured content. This will force all API clients to reload featured idols,
                studios, categories, and videos from the database.
              </p>
              <div className='flex items-center gap-2'>
                <Button
                  variant='destructive'
                  size='sm'
                  onClick={handleClearFeaturedCache}
                  disabled={clearFeaturedCacheMutation.isPending}
                >
                  <Trash2 className='h-4 w-4 mr-2' />
                  {clearFeaturedCacheMutation.isPending ? 'Clearing...' : 'Clear Cache'}
                </Button>
                {clearFeaturedCacheMutation.isSuccess && (
                  <span className='text-sm text-green-600 dark:text-green-500'>✓ Cache cleared successfully!</span>
                )}
                {clearFeaturedCacheMutation.isError && (
                  <span className='text-sm text-destructive'>✗ Failed to clear cache</span>
                )}
              </div>
            </div>
          </div>

          <div className='border-t pt-4'>
            <h4 className='font-semibold mb-2 text-sm'>When to clear cache:</h4>
            <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
              <li>After updating featured content in the Featured page</li>
              <li>When featured items are not showing correctly on the main site</li>
              <li>After bulk updates to idols, studios, or categories</li>
              <li>When testing featured content changes</li>
            </ul>
          </div>

          <div className='bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3'>
            <p className='text-xs text-yellow-800 dark:text-yellow-500'>
              ⚠️ <strong>Note:</strong> Clearing cache is safe but will temporarily increase database load as clients
              rebuild their cache. The cache will automatically regenerate on the next request.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Future Settings Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Settings</CardTitle>
          <CardDescription>More configuration options coming soon</CardDescription>
        </CardHeader>
        <CardContent className='text-center py-8 text-muted-foreground'>
          <p>Future settings will be added here:</p>
          <ul className='mt-4 space-y-2 text-sm'>
            <li>• Maximum comment length</li>
            <li>• Rate limiting (comments per day)</li>
            <li>• Spam detection settings</li>
            <li>• Feature flags (enable/disable comments, ratings, etc.)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
